import DBModal from 'components/modal/modal';
import {walletCurrencies} from 'utils/mock';
import styles from './payouts.module.scss';
import {DatePicker, Form, Input, InputNumber, Select, Spin, Switch} from 'antd';
import {
  BULK_UPLOAD_PAYOUT,
  EXPORT_CSV_USERS_PAYOUT,
  GET_INVESTED_USER_BY_DATA_RANGE,
  PROCESS_SINGLE_USER_PAYOUT,
} from 'graphql/queries/dividends';
import {useLazyQuery, useMutation} from '@apollo/client';
import moment from 'moment';
import {handleUpdateDividendId} from 'redux/store/app';
import {useDispatch} from 'react-redux';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {useEffect, useMemo, useState} from 'react';
import {UploadVideoToBucket} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';
import {UserSelectField} from 'components/userSelectField/UserSelectField';
import {
  handleCSVPayoutUpload,
  handleDefaultPayout,
  handleProcessCSVPayout,
  handleSingleUser,
} from './handlePayoutFunctions';
import {toast} from 'react-toastify';
import {InstrumentFields} from './instrumentPicker';

export const ProcessDividendPayout = ({
  openPayoutModal,
  onClose,
  activeTab,
  form,
  onFilter,
  resetPage,
  handleFilterValues,
}) => {
  const [loadingCSVUpload, setLoadingCSVUpload] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [csvUrl, setCSVUrl] = useState('');
  const [csvName, setCsvName] = useState('');
  const type = Form.useWatch('type', form);
  const dispatch = useDispatch();
  const [user, setUser] = useState([]);
  const exportEmail = Form.useWatch('export', form);

  useEffect(() => {
    if (!openPayoutModal) {
      setCSVUrl(null);
      setCsvName('');
    }
  }, [openPayoutModal]);

  // RESOLVERS
  const [getInvestedUsersByDateRange, {loading: loadingInvestedUsers}] =
    useLazyQuery(GET_INVESTED_USER_BY_DATA_RANGE, {
      errorPolicy: 'all',
    });
  const [bulkUploadDividendPayout, {loading: loadingBulkPayout}] = useMutation(
    BULK_UPLOAD_PAYOUT,
    {
      errorPolicy: 'all',
      onError: error => toast.error('Something went wrong', error.message),
    },
  );
  const [addDividendPayout, {loading: loadingSinglePayout}] = useMutation(
    PROCESS_SINGLE_USER_PAYOUT,
    {
      errorPolicy: 'all',
      onError: error => toast.error('Something went wrong', error.message),
    },
  );

  const [generateAndExportInvestedUsers, {loading: loadingExportCSV}] =
    useLazyQuery(EXPORT_CSV_USERS_PAYOUT, {
      errorPolicy: 'all',
      onError: error => toast.error('Something went wrong: ', error.message),
    });

  const handlePayout = values => {
    const {
      startDate,
      endDate,
      instrumentId,
      type,
      email,
      export: exportEmail,
    } = values;
    if (activeTab !== 'PAYOUT' || openPayoutModal?.type === 'UNPAID_SEARCH') {
      const validFields = {
        // `instrumentId` only. getDividendHistory rejects companyId and
        // investmentCategory, so an unset filter must omit the key entirely
        // rather than send it empty.
        ...(instrumentId && {instrumentId}),
        ...(exportEmail && {email, export: exportEmail}),
        ...(startDate && {
          startDate: moment(startDate).format(),
        }),
        ...(endDate && {
          endDate: moment(endDate).format(),
        }),
        ...(user.length > 0 && {userId: user?.split(';')[2]}),
      };
      handleFilterValues({
        ...validFields,
      });
      onFilter(
        {
          ...validFields,
        },
        1,
      );
      resetPage();
      return onClose();
    }

    if (type === 'DEFAULT' && openPayoutModal?.type !== 'CSV') {
      handleDefaultPayout(
        values,
        getInvestedUsersByDateRange,
        dispatch,
        handleUpdateDividendId,
        onClose,
        form,
      );
    } else if (type === 'CSV_UPLOAD') {
      handleCSVPayoutUpload(
        values,
        csvUrl,
        setCSVUrl,
        bulkUploadDividendPayout,
        dispatch,
        handleUpdateDividendId,
        onClose,
        form,
      );
    } else if (type === 'USER') {
      handleSingleUser(
        values,
        addDividendPayout,
        dispatch,
        onClose,
        handleUpdateDividendId,
        user,
        form,
      );
    } else {
      handleProcessCSVPayout(
        values,
        generateAndExportInvestedUsers,
        onClose,
        form,
      );
    }
  };

  const handleCSVUPload = async e => {
    setUploadProgress(0);
    const selectedFile = e.target.files[0];
    setCsvName(selectedFile.name);
    setLoadingCSVUpload(true);
    const response = await UploadVideoToBucket(
      selectedFile,
      'campaign-bucket-images',
      setUploadProgress,
    );
    setCSVUrl(response);
    setLoadingCSVUpload(false);
  };

  const modalHeader = useMemo(() => {
    if (openPayoutModal?.type === 'CSV') {
      return 'Process & Export Dividends Payout';
    }
    if (openPayoutModal?.type === 'FINALIZE') {
      return 'Finalize Dividend Payout';
    }
    if (openPayoutModal?.type === 'UNPAID_SEARCH') {
      return 'Filter Unpaid Dividends';
    }
    if (openPayoutModal?.type === 'PAID_SEARCH') {
      return 'Search Paid Dividends';
    }
    return '';
  }, [openPayoutModal?.type]);

  return (
    <DBModal
      isOpen={openPayoutModal.isOpen}
      handleClose={onClose}
      width={800}
      content={
        <div className={styles.modal}>
          <h1>{modalHeader}</h1>
          <Form form={form} onFinish={handlePayout} layout="vertical">
            {['FINALIZE'].includes(openPayoutModal?.type) && (
              <Form.Item
                initialValue="DEFAULT"
                name="type"
                label="Type of Payout">
                <Select defaultValue="DEFAULT">
                  <Select.Option key="DEFAULT">Default</Select.Option>
                  {openPayoutModal?.type !== 'CSV' && (
                    <>
                      <Select.Option key="CSV_UPLOAD">CSV Upload</Select.Option>
                      <Select.Option key="USER">Single User</Select.Option>
                    </>
                  )}
                </Select>
              </Form.Item>
            )}
            {type === 'CSV_UPLOAD' && activeTab === 'PAYOUT' && (
              <div className={styles.uploadSec}>
                <label className={styles.upload} htmlFor="file-upload">
                  {loadingCSVUpload ? (
                    <div className={styles.uploading}>
                      <Spin />
                      <h3>Uploading, Please wait...</h3>
                      <span>{uploadProgress}%</span>
                    </div>
                  ) : csvName && csvUrl ? (
                    <div>{csvName}</div>
                  ) : (
                    <h2>Upload CSV File</h2>
                  )}
                  <input
                    type="file"
                    id="file-upload"
                    accept=".csv"
                    className={styles['input-file']}
                    required
                    onChange={handleCSVUPload}
                  />
                </label>
                <div>
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href="https://dabacdn-bucket001.s3.us-east-1.amazonaws.com/contents/dividend-samplee.csv">
                    Sample CSV
                  </a>
                </div>
              </div>
            )}
            {type === 'USER' ||
              (['UNPAID_SEARCH', 'PAID_SEARCH'].includes(
                openPayoutModal?.type,
              ) && (
                <Form.Item name="user" label="Select User">
                  <UserSelectField
                    users={user}
                    setUsers={setUser}
                    mode="single"
                  />
                </Form.Item>
              ))}
            <InstrumentFields
              form={form}
              required={
                activeTab === 'PAYOUT' &&
                openPayoutModal?.type !== 'UNPAID_SEARCH'
              }
            />
            {activeTab === 'PAYOUT' &&
              openPayoutModal?.type !== 'UNPAID_SEARCH' && (
                <>
                  <Form.Item
                    rules={[
                      {required: true, message: 'This is a Required Field'},
                    ]}
                    className={styles.field}
                    name="assetCurrency"
                    label="Currency">
                    <Select>
                      {walletCurrencies.map(data => (
                        <Select.Option key={data.key}>{data.key}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {type !== 'CSV_UPLOAD' && openPayoutModal?.type !== 'CSV' && (
                    <div className={styles.flexWidth}>
                      <Form.Item
                        className={styles.field}
                        name="principalPerShare"
                        label="Principle Per Share">
                        <Input className={styles.smallInput} type="number" />
                      </Form.Item>
                      <Form.Item
                        rules={[
                          {required: true, message: 'This is a Required Field'},
                        ]}
                        className={styles.field}
                        name="amountPerShare"
                        label="Amount Per Share">
                        <Input className={styles.smallInput} type="number" />
                      </Form.Item>
                    </div>
                  )}
                  {openPayoutModal?.type === 'FINALIZE' && (
                    <div className={styles.flexWidth}>
                      <Form.Item name="taxName" label="Tax Name">
                        <Input className={styles.smallInput} />
                      </Form.Item>
                      <Form.Item name="taxPercentage" label="Tax Percentage">
                        <InputNumber
                          className="input-number"
                          min={0}
                          max={100}
                        />
                      </Form.Item>
                    </div>
                  )}
                  {type === 'USER' && (
                    <Form.Item
                      rules={[
                        {required: true, message: 'This is a Required Field'},
                      ]}
                      className={styles.field}
                      name="units"
                      label="Units">
                      <Input className={styles.smallInput} type="number" />
                    </Form.Item>
                  )}
                </>
              )}
            <div className={styles.dates}>
              <Form.Item
                rules={[
                  {
                    required:
                      activeTab === 'PAYOUT' &&
                      openPayoutModal?.type !== 'UNPAID_SEARCH',
                    message: 'This is a Required Field',
                  },
                ]}
                className={styles.field}
                name="startDate"
                label="Start Date">
                <DatePicker
                  allowClear
                  className={styles.smallInput}
                  format={'YYYY-MM-DD HH:mm:ss'}
                  showTime={{
                    defaultValue: moment('00:00', 'HH:mm:ss'),
                  }}
                />
              </Form.Item>
              <Form.Item
                rules={[
                  {
                    required:
                      activeTab === 'PAYOUT' &&
                      openPayoutModal?.type !== 'UNPAID_SEARCH',
                    message: 'This is a Required Field',
                  },
                ]}
                className={styles.field}
                name="endDate"
                label="End Date">
                <DatePicker
                  allowClear
                  className={styles.smallInput}
                  format={'YYYY-MM-DD HH:mm:ss'}
                  showTime={{
                    defaultValue: moment('00:00', 'HH:mm:ss'),
                  }}
                />
              </Form.Item>
            </div>
            <Form.Item className={styles.field} name="export" label="Export">
              <Switch checked={exportEmail} />
            </Form.Item>
            {exportEmail && (
              <Form.Item
                rules={[
                  {
                    type: 'email',
                    required: true,
                    message: 'Please enter a valid email address',
                  },
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }
                      if (!value.endsWith('@joindaba.com')) {
                        return Promise.reject(
                          new Error('Only daba emails are allowed'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                className={styles.field}
                name="email"
                label="Email">
                <Input
                  placeholder="Input a daba email"
                  className={styles.smallInput}
                  type="email"
                />
              </Form.Item>
            )}

            <SubmitButton
              disabled={
                loadingInvestedUsers ||
                loadingBulkPayout ||
                loadingSinglePayout ||
                loadingExportCSV
              }
              label={
                activeTab === 'PAYOUT' &&
                openPayoutModal?.type !== 'UNPAID_SEARCH'
                  ? openPayoutModal?.type === 'CSV'
                    ? 'Export Payouts'
                    : 'Finalize Payouts'
                  : 'Filter'
              }
            />
          </Form>
        </div>
      }
    />
  );
};
